/**
 * Given a price and a sale price(special price), calculate the percent off.
 * @param {*} price 
 * @param {*} specialPrice 
 * @returns {number} percentOff
 */
export function calculatePercentOffPrice(price, specialPrice) {
	let percentOff = Math.round((1 - (specialPrice / price)) * 100);
	return percentOff;
}

/**
 * Given the location hours object, return true if the location is currently open.
 * Return false if the location is currently closed.
 * @param {*} locationHours 
 * @returns 
 */
export function isLocationCurrentlyOpen(locationHours) {
	const now = new Date();
	const startTime = new Date();
	const endTime = new Date();

	if ( locationHours.start == "open" || locationHours.end == "open") {
		return true; // for sandbox
	}

	// Split the start and end time strings into hours, minutes, and AM/PM.
	const [startHours, startMinutes, startPeriod] = locationHours.start.split(/:| /);
	const [endHours, endMinutes, endPeriod] = locationHours.end.split(/:| /);

	// Convert the hours and minutes to numbers.
	let parsedStartHours = parseInt(startHours, 10);
	const parsedStartMinutes = parseInt(startMinutes, 10);
	let parsedEndHours = parseInt(endHours, 10);
	const parsedEndMinutes = parseInt(endMinutes, 10);

	// Adjust the parsed hours based on AM/PM.
	if (startPeriod.toLowerCase() === "pm" && parsedStartHours !== 12) {
		parsedStartHours += 12;
	}
	if (endPeriod.toLowerCase() === "pm" && parsedEndHours !== 12) {
		parsedEndHours += 12;
	}

	startTime.setHours(parsedStartHours, parsedStartMinutes);
	endTime.setHours(parsedEndHours, parsedEndMinutes);

	// Check if the current time is within the opening hours.
	if (now >= startTime && now <= endTime) {
		return true;
	} else {
		return false;
	}
}

/**
 * Use locationHours object to get the Opening time.
 * @param {*} locationHours 
 * @returns 
 */
export function getOpenTime(locationHours) {
	if ( locationHours.start == "open" || locationHours.end == "open") {
		return true;
	}

	const [startHours, startMinutes, startPeriod] = locationHours.start.split(/:| /);
	let parsedStartHours = parseInt(startHours, 10);
	const parsedStartMinutes = parseInt(startMinutes, 10);

	if (startPeriod.toLowerCase() === "pm" && parsedStartHours !== 12) {
		parsedStartHours += 12;
	}

	const startTime = new Date();
	startTime.setHours(parsedStartHours, parsedStartMinutes);

	const formattedTime = startTime.toLocaleString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true
	});

	return formattedTime;
}

/**
 * Use locationHours object to get the Closing time.
 * @param {*} locationHours 
 * @returns 
 */
export function getCloseTime(locationHours) {
	if ( locationHours.start == "open" || locationHours.end == "open") {
		return false;// for sandbox
	}
	const [endHours, endMinutes, endPeriod] = locationHours.end.split(/:| /);
	let parsedEndHours = parseInt(endHours, 10);
	const parsedEndMinutes = parseInt(endMinutes, 10);

	if (endPeriod.toLowerCase() === "pm" && parsedEndHours !== 12) {
		parsedEndHours += 12;
	}

	const endTime = new Date();
	endTime.setHours(parsedEndHours, parsedEndMinutes);

	const formattedTime = endTime.toLocaleString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true
	});

	return formattedTime;
}

/**
 * Get the current day of the week.
 * @returns 
 */
export function getDayOfWeek() {
	const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
	const today = new Date();
	const dayOfWeek = daysOfWeek[today.getDay()];
	return dayOfWeek;
}

/**
 * Format the total price to be displayed.
 * @param {*} str 
 * @returns 
 */
export function formatTotal(str) {
	if (str == 0) {
		return "0";
	}
	str = String(str);

	return str.substring(0, str.length - 2) + "." + str.substring(str.length - 2);
}

export function formatter(number) {
	const formatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	});
	let str = formatter.format(number);
	return str.replace(/\.00$/, "");
}

export function formatterWithZeroes(number) {
	const formatter = new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2
	});
	let str = formatter.format(number);
	return str;
}

export function costOfCheckoutItem(item) {
	const variant = item.product.variants.find(
		(variant) => variant.option === item.option
	);

	const itemPrice = variant?.specialPriceRec ?? variant?.priceRec ?? 0;
	return formatterWithZeroes(itemPrice);
}

export function productSlugFormat(product, retailer) {

	let city = retailer.city.replace(/\s+/g, '-').toLowerCase();

	return `${product.slug}-for-sale-in-${city}-${retailer.state.toLowerCase()}-${product.id}`;
}


export function categoryEnum(str) {
	return str.toUpperCase().replace(/\s+/g, "_").replace(/-/g, "_")
}

export function convertStringToTitleCase(str) {
	// Replace underscores with spaces
	str = str.replace(/_/g, " ");

	// Convert to title case
	str = str
		.toLowerCase()
		.split(" ")
		.map(function (word) {
			return word.charAt(0).toUpperCase() + word.slice(1);
		})
		.join(" ");

	return str;
}

export function scrollToTop() {
	return new Promise((resolve) => {
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
		setTimeout(() => {
			resolve();
		}, 500);
	});
}

const stateMap = {
	AL: "Alabama",
	AK: "Alaska",
	AZ: "Arizona",
	AR: "Arkansas",
	CA: "California",
	CO: "Colorado",
	CT: "Connecticut",
	DE: "Delaware",
	FL: "Florida",
	GA: "Georgia",
	HI: "Hawaii",
	ID: "Idaho",
	IL: "Illinois",
	IN: "Indiana",
	IA: "Iowa",
	KS: "Kansas",
	KY: "Kentucky",
	LA: "Louisiana",
	ME: "Maine",
	MD: "Maryland",
	MA: "Massachusetts",
	MI: "Michigan",
	MN: "Minnesota",
	MS: "Mississippi",
	MO: "Missouri",
	MT: "Montana",
	NE: "Nebraska",
	NV: "Nevada",
	NH: "New Hampshire",
	NJ: "New Jersey",
	NM: "New Mexico",
	NY: "New York",
	NC: "North Carolina",
	ND: "North Dakota",
	OH: "Ohio",
	OK: "Oklahoma",
	OR: "Oregon",
	PA: "Pennsylvania",
	RI: "Rhode Island",
	SC: "South Carolina",
	SD: "South Dakota",
	TN: "Tennessee",
	TX: "Texas",
	UT: "Utah",
	VT: "Vermont",
	VA: "Virginia",
	WA: "Washington",
	WV: "West Virginia",
	WI: "Wisconsin",
	WY: "Wyoming",
};

export function getStateName(stateAbbreviation) {
	return stateMap[stateAbbreviation];
}

export function trimToThree(str) {
	return String(str).substring(0, 3);
}

export function getStrainTypes() {
	const strainTypes = [
		{
			name: "Indica",
			value: "INDICA",
		},
		{
			name: "Sativa",
			value: "SATIVA",
		},
		{
			name: "Hybrid",
			value: "HYBRID",
		},
		{
			name: "High CBD",
			value: "HIGH_CBD",
		},
	];
	return strainTypes;
}

export function formatStrainType(str) {
	//replace underscores with spaces, capitalize first letter
	let strainType = str.replace("_", " ").toLowerCase();
	let words = strainType.split(" ");

	//capitalize the first letter in each word
	for (let i = 0; i < words.length; i++) {
		words[i] = words[i][0].toUpperCase() + words[i].substring(1);

		// Check for 'Cbd' abbreviation and capitalize it
		if (words[i].toLowerCase() === 'cbd') {
			words[i] = 'CBD';
		  }

	}
	return words.join(" ");
}

export function generatePageArray(pages, currentPage) {
	currentPage = parseInt(currentPage);

	const MAX_VISIBLE_PAGES = 5;
	let pageArray = [];

	if ( pages <= MAX_VISIBLE_PAGES ) {
		pageArray = Array.from({ length: pages }, (_, i) => i + 1);
	} else {

		if ( currentPage < 4 ) {
			pageArray = [1, 2, 3, 4, '...', pages];
		} else if ( getDistanceFromNumber(currentPage, pages) < 3 ) {
			pageArray = [1, '...', pages - 3, pages - 2, pages - 1, pages];
		} else {
			pageArray = [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', pages];
		}
	}
	return pageArray;
}

export function getDistanceFromNumber(x, number) {
	return Math.abs(x - number);
}

export const sortValues = {
	POPULAR: { 
		value: "POPULAR", 
		label: "Popularity", 
		direction: "DESC",
		key: 'POPULAR',
	},
	NAME: { 
		value: "NAME", 
		label: "Name: A-Z", 
		direction: "ASC",
		key: 'NAME',
	},
	NAME_DESC: {
		value: "NAME",
		label: "Name: Z-A",
		direction: "DESC",
		key: 'NAME_DESC',
	},
	PRICE_LOW: {
		value: "PRICE",
		label: "Low to High Price",
		direction: "ASC",
		key: 'PRICE_LOW',
	},
	PRICE_HIGH: {
		value: "PRICE",
		label: "High to Low Price",
		direction: "DESC",
		key: 'PRICE_HIGH',
	},
	POTENCY_LOW: {
		value: "POTENCY",
		label: "Low to High Potency",
		direction: "ASC",
		key: 'POTENCY_LOW',
	},
	POTENCY_HIGH: {
		value: "POTENCY",
		label: "High to Low Potency",
		direction: "DESC",
		key: 'POTENCY_HIGH',
	},
};

